import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getCategories } from '@/lib/api';
import { EcwidLink, ecwidCategoryUrl } from '@/components/EcwidWidgets';
import { Phone, MapPin, Clock } from 'lucide-react';
import { LOGO_URL, BUSINESS } from '@/data/site';
import { LOCATIONS } from '@/data/locations';

const SHOP_LINKS = [
  { to: '/shop/exotic', label: 'Exotic Flower' },
  { to: '/shop/prerolls', label: 'Pre-Rolls' },
  { to: '/shop/cartridges', label: 'Cartridges' },
  { to: '/shop/disposable-vapes', label: 'Disposable Vapes' },
  { to: '/shop/edibles', label: 'Edibles' },
  { to: '/shop/concentrates', label: 'Concentrates' },
  { to: '/shop/psilocybin-mushroom', label: 'Mushrooms' },
  { to: '/shop/shake', label: 'Shake' },
  { to: '/deals', label: 'Deals & Bundles' },
];

const COMPANY_LINKS = [
  { to: '/how-to-order', label: 'How to Order' },
  { to: '/service-area', label: 'Service Area' },
  { to: '/faq', label: 'FAQ' },
  { to: '/about', label: 'About Us' },
  { to: '/blog', label: 'Blog' },
  { to: '/contact', label: 'Contact' },
  { to: '/cannabis-washington-dc', label: 'Cannabis in Washington, DC' },
  { to: '/cannabis-delivery-washington-dc', label: 'Cannabis Delivery DC' },
];

export default function Footer() {
  const { data: categories } = useQuery({ queryKey: ['categories'], queryFn: getCategories, staleTime: 120000 });
  const hrefFor = (to) => {
    if (!to.startsWith('/shop/')) return to;
    const cat = categories?.find((c) => c.slug === to.replace('/shop/', ''));
    return cat ? ecwidCategoryUrl(cat) : to;
  };
  return (
    <footer data-testid="site-footer" className="border-t border-ink/10 bg-surface pb-24 lg:pb-0">
      <div className="mx-auto max-w-7xl px-6 py-16">
        <div className="grid gap-12 md:grid-cols-2 lg:grid-cols-4">
          <div>
            <Link to="/" data-testid="footer-logo-link" className="flex items-center gap-3">
              <img src={LOGO_URL} alt="Majestic Greens DMV logo" className="h-12 w-12 rounded-full" />
              <span className="font-serif text-xl text-ink">Majestic <span className="text-bronze">Greens</span> DMV</span>
            </Link>
            <p className="mt-5 text-sm leading-relaxed text-sage">
              Premium cannabis retail serving Washington, DC since {BUSINESS.founded}. Quality products, easy online ordering, and service you can trust.
            </p>
          </div>

          <div>
            <h3 className="text-[11px] font-bold uppercase tracking-[0.3em] text-bronze">Shop</h3>
            <ul className="mt-5 space-y-3">
              {SHOP_LINKS.map((l) => (
                <li key={l.to}>
                  <EcwidLink to={hrefFor(l.to)} data-testid={`footer-shop-${l.label.toLowerCase().replace(/[^a-z]+/g, '-')}`} className="text-sm text-sage transition-colors hover:text-bronze">{l.label}</EcwidLink>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-[11px] font-bold uppercase tracking-[0.3em] text-bronze">Areas We Serve</h3>
            <ul className="mt-5 space-y-3">
              {LOCATIONS.map((l) => (
                <li key={l.slug}>
                  <Link to={`/${l.slug}`} data-testid={`footer-location-${l.slug}`} className="text-sm text-sage transition-colors hover:text-bronze">
                    Cannabis near {l.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-[11px] font-bold uppercase tracking-[0.3em] text-bronze">Visit & Contact</h3>
            <address className="mt-5 space-y-4 not-italic">
              <p className="flex items-start gap-3 text-sm text-sage">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-bronze" />
                {BUSINESS.name}<br />
                {BUSINESS.address}<br />
                {BUSINESS.city}, {BUSINESS.state} {BUSINESS.zip}
              </p>
              <a href={BUSINESS.phoneHref} data-testid="footer-phone-link" className="flex items-center gap-3 text-sm text-sage transition-colors hover:text-bronze">
                <Phone className="h-4 w-4 text-bronze" /> {BUSINESS.phone}
              </a>
              <p className="flex items-center gap-3 text-sm text-sage">
                <Clock className="h-4 w-4 text-bronze" /> Open Daily 10:00 AM – 11:00 PM
              </p>
            </address>
            <ul className="mt-6 space-y-3">
              {COMPANY_LINKS.map((l) => (
                <li key={l.to}>
                  <Link to={l.to} data-testid={`footer-company-${l.label.toLowerCase().replace(/[^a-z]+/g, '-')}`} className="text-sm text-sage transition-colors hover:text-bronze">{l.label}</Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-14 border-t border-ink/10 pt-8">
          <p className="text-xs leading-relaxed text-ash">
            For adults 21 years of age and older only. Please consume responsibly. Keep all products out of reach of children and pets.
            Do not operate a vehicle or machinery under the influence. Cannabis products are not intended to diagnose, treat, cure, or prevent any disease.
          </p>
          <p className="mt-4 text-xs text-ash">
            © {new Date().getFullYear()} {BUSINESS.name} · {BUSINESS.address}, {BUSINESS.city}, {BUSINESS.state} {BUSINESS.zip} · {BUSINESS.phone}
          </p>
        </div>
      </div>
    </footer>
  );
}
